/**
 * ============================================================
 * KEJJA OS — src/pages/finance/BillsPage.tsx  (v1.1)
 * Recurring bills that drive "Safe to Spend"
 * ============================================================
 */

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Plus, Trash2, Calendar, CheckCircle2 } from 'lucide-react';

import { useFinanceContext } from './FinanceLayout';
import {
  useFinanceBills, useCreateBill, useDeleteBill, useMarkBillPaid,
} from '@/hooks/useFinance';
import type { BillCadence } from '@/lib/financeApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

const CADENCE_LABELS: Record<BillCadence, string> = {
  weekly: 'Weekly', monthly: 'Monthly', termly: 'Every Term',
  annual: 'Annual', one_off: 'One-off',
};

export default function BillsPage() {
  const { homeId } = useFinanceContext();
  const { data: bills, isLoading } = useFinanceBills(homeId);
  const createBill = useCreateBill(homeId);
  const deleteBill = useDeleteBill(homeId);
  const markPaid = useMarkBillPaid(homeId);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '', amount: '', cadence: 'monthly' as BillCadence,
    nextDueDate: new Date().toISOString().slice(0, 10),
    isHardBill: true,
  });

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    const amountMinor = Math.round(parseFloat(form.amount) * 100);
    if (!form.name.trim() || !amountMinor || !form.nextDueDate) return;

    createBill.mutate({
      name: form.name.trim(),
      amount: amountMinor,
      cadence: form.cadence,
      nextDueDate: form.nextDueDate,
      isHardBill: form.isHardBill,
    }, {
      onSuccess: () => {
        setShowAdd(false);
        setForm({ name: '', amount: '', cadence: 'monthly', nextDueDate: new Date().toISOString().slice(0, 10), isHardBill: true });
      },
    });
  }

  return (
    <div className="space-y-5 pb-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Bills</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Recurring obligations — these drive your "Safe to Spend"
          </p>
        </div>
        <Button
          size="sm"
          className="bg-orange-600 hover:bg-orange-700 text-white"
          onClick={() => setShowAdd(!showAdd)}
        >
          <Plus className="w-4 h-4 mr-1" /> Add bill
        </Button>
      </div>

      {showAdd && (
        <Card className="border-orange-200 bg-orange-50/30 dark:bg-orange-950/10">
          <CardContent className="p-4">
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <Label htmlFor="bill-name" className="text-xs">Name</Label>
                <Input
                  id="bill-name"
                  placeholder="e.g. Rent, KPLC, Form 2 School Fees"
                  value={form.name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bill-amt" className="text-xs">Amount (KSh)</Label>
                  <Input
                    id="bill-amt" type="number" step="0.01"
                    value={form.amount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, amount: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="bill-due" className="text-xs">Next due</Label>
                  <Input
                    id="bill-due" type="date"
                    value={form.nextDueDate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, nextDueDate: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs block mb-1.5">Cadence</Label>
                <div className="grid grid-cols-5 gap-1">
                  {(Object.entries(CADENCE_LABELS) as Array<[BillCadence, string]>).map(([key, lbl]) => (
                    <button
                      key={key} type="button"
                      onClick={() => setForm({ ...form, cadence: key })}
                      className={cn(
                        'px-2 py-1.5 text-[11px] rounded-lg border font-medium',
                        form.cadence === key
                          ? 'bg-orange-600 text-white border-orange-600'
                          : 'border-border bg-background hover:bg-muted',
                      )}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.isHardBill}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, isHardBill: e.target.checked })}
                  className="rounded border-border"
                />
                <span>This is a hard bill (counts against Safe to Spend)</span>
              </label>
              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={createBill.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                  {createBill.isPending ? 'Adding…' : 'Add bill'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
      ) : !bills || bills.bills.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Calendar className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No bills yet. Add Rent, KPLC, School Fees, Internet — anything that repeats.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {bills.bills.map((b) => {
              const due = new Date(b.nextDueDate + 'T00:00:00Z');
              const daysUntil = Math.ceil((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              const overdue = daysUntil < 0;

              return (
                <div key={b.id} className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    {b.name[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{b.name}</span>
                      <Badge variant="outline" className="text-[9px] font-normal">
                        {CADENCE_LABELS[b.cadence]}
                      </Badge>
                      {!b.isHardBill && (
                        <Badge variant="outline" className="text-[9px] font-normal">soft</Badge>
                      )}
                    </div>
                    <div className={cn('text-[11px] mt-0.5 font-medium', overdue ? 'text-rose-600' : 'text-muted-foreground')}>
                      {overdue ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'Due today' : `Due in ${daysUntil}d`} · {formatMoney(b.amount, b.currencyCode)}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      size="sm" variant="outline" className="h-8 text-xs"
                      onClick={() => markPaid.mutate(b.id)}
                      disabled={markPaid.isPending}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Paid
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600"
                      onClick={() => { if (confirm(`Delete "${b.name}"?`)) deleteBill.mutate(b.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
